import React from 'react';
import PropTypes from 'prop-types';

import {
	Wrapper,
	Title,
	Date,
	MetaActions,
	MetaWrapper,
	VerifiedWrapper,
	VerifiedText,
	MetaText,
	ThanksText,
	Ratings,
	FlagWrapper,
	FlagButton,
	HelpfulWrapper,
	InlineEditButtonWrapper,
	HelpButton,
} from './reviewStyles';
// Import icons by individual modules for better code splitting
import Verified from 'core/icons/Verified';
import ThumbsUp from 'core/icons/ThumbsUp';

import LoginModalHoc from 'core/hoc/LoginModalHoc';
import MerchantReply from 'core/components/MerchantReply';
import TruncatedContent from 'core/components/TruncatedContent';

import ReportConfirm from './ReportConfirm';
import ReviewImages from './ReviewImages';
import EditReviewButton from "./EditReviewButton";

import Rating from './Rating';
import AuthorSummary from './AuthorSummary';
import loadable from '@loadable/component';
const AdminTools = loadable(() => import('./AdminTools'));

const ReviewMarkup = ({
	// Normal props
	id,
	rating,
	shipping,
	quality,
	curation,
	date,
	title,
	body,
	verified,
	response,
	responseDate,
	responseName,
	isPreview,
	images,
	authorIsCurrentCustomer,
	visibleWordCount,
	author,
	votes,
	isAdmin,
	onDelete,
	onBoost,
	createdAtAgo,
	isCustomer,
	minimal,
	boosted,
	// Stateful props from Container
	handleHover,
	onClickEdit,
	handleReport,
	onReportClose,
	handleHelpful,
	hoveredOver,
	helpful,
	report_confirm,
	report,
}) => (
	<Wrapper onMouseOver={handleHover(true)} onMouseOut={handleHover(false)} isPreview={isPreview}>
		<InlineEditButtonWrapper>
			<Title>{title}</Title>
			{authorIsCurrentCustomer && <EditReviewButton hovered={hoveredOver} onClick={onClickEdit}/>}
		</InlineEditButtonWrapper>
		<Date>{createdAtAgo || date}</Date>
		<Ratings>
			<Rating label="Overall" score={rating} minimal={minimal} />
			{! minimal && (
				<>
					<Rating label="Shipping" score={shipping} />
					<Rating label="Quality" score={quality} />
					<Rating label="Curation" score={curation} />
				</>
			)}
			{minimal && verified && (
				<VerifiedWrapper>
					<Verified />
					<VerifiedText>Verified Purchase</VerifiedText>
				</VerifiedWrapper>
			)}
		</Ratings>
		<AuthorSummary {...author} verified={verified} />
		<TruncatedContent visibleWordCount={visibleWordCount} large>
			{body}
		</TruncatedContent>

		{!minimal && verified && (
			<VerifiedWrapper>
				<Verified />
				<VerifiedText>Verified Purchase</VerifiedText>
			</VerifiedWrapper>
		)}

		{images && images.length > 0 && <ReviewImages images={images} />}

		<LoginModalHoc contextUsage="flag-helpful-login">
			{({ triggerLoginModal }) =>
				<MetaWrapper>
					<MetaActions>
						<HelpfulWrapper>
							{helpful ? <ThanksText>Thank you for your feedback</ThanksText> : (
								<HelpButton size="md" themeType="antistroke" onClick={handleHelpful(triggerLoginModal)}>
									<ThumbsUp fill="currentColor" />
									<span>Helpful {votes > 0 && `(${votes})`}</span>
								</HelpButton>
							)}
						</HelpfulWrapper>
						{isAdmin && (
							<AdminTools reviewId={id} onDelete={onDelete} onBoost={onBoost} boosted={boosted}/>
						)}
						<FlagWrapper>
							{report_confirm ? <MetaText>Reported</MetaText> : (
								<FlagButton href="#report-abuse" onClick={e => handleReport(e, triggerLoginModal)}>
											Flag
								</FlagButton>
							)}
						</FlagWrapper>
					</MetaActions>
				</MetaWrapper>
			}
		</LoginModalHoc>

		{response && (
			<LoginModalHoc contextUsage="contact-seller-merch-reply-login">
				{({ triggerLoginModal }) =>
					<MerchantReply triggerLogin={triggerLoginModal} isLoggedIn={isCustomer} postDate={responseDate} displayName={responseName}>
						{response}
					</MerchantReply>
				}
			</LoginModalHoc>
		)}

		{report && (
			<ReportConfirm
				onClose={onReportClose}
				onConfirm={handleReport}
			/>
		)}
	</Wrapper>
);

export const reviewProps = {
	id: PropTypes.number.isRequired,
	name: PropTypes.string.isRequired,
	rating: PropTypes.number.isRequired,
	shipping: PropTypes.number,
	quality: PropTypes.number,
	curation: PropTypes.number,
	date: PropTypes.string.isRequired,
	votes: PropTypes.number.isRequired,
	title: PropTypes.string,
	body: PropTypes.string.isRequired,
	verified: PropTypes.bool.isRequired,
	slug: PropTypes.string.isRequired,
	response: PropTypes.string,
	responseDate: PropTypes.string,
	responseName: PropTypes.string,
	isCustomer: PropTypes.bool,
	isPreview: PropTypes.bool,
	authorIsCurrentCustomer: PropTypes.bool.isRequired,
	onEditReview: PropTypes.func,
	onFeedback: PropTypes.func,
	images: PropTypes.arrayOf(PropTypes.shape({
		id: PropTypes.number.isRequired,
		altText: PropTypes.string,
		rank: PropTypes.number.isRequired,
		lowResUrl: PropTypes.string.isRequired,
		highResUrl: PropTypes.string.isRequired,
	})),
	visibleWordCount: PropTypes.number,
	author: PropTypes.object,
	createdAtAgo: PropTypes.string,
	editedAtAgo: PropTypes.string,
	isAdmin: PropTypes.bool,
	onDelete: PropTypes.func,
	onBoost: PropTypes.func,
	minimal: PropTypes.bool,
	boosted: PropTypes.bool,
};

ReviewMarkup.propTypes = {
	...reviewProps,
	// Stateful props
	handleHover: PropTypes.func,
	onClickEdit: PropTypes.func,
	handleReport: PropTypes.func,
	onReportClose: PropTypes.func,
	handleHelpful: PropTypes.func,
	hoveredOver: PropTypes.bool,
	helpful: PropTypes.bool,
	report_confirm: PropTypes.bool,
	report: PropTypes.bool,
};

ReviewMarkup.defaultProps = {
	authorIsCurrentCustomer: false,
	visibleWordCount: 250,
};

export default ReviewMarkup;
